import { FC } from "react";
import {
    Button,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    VStack,
} from "@chakra-ui/react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useToast } from "@am-tax/fe-core";
import { CandidateCustomFields, EntityCandidateForUpdate } from "../../api/models/EntityCandidate";
import { CandidateFieldsConfiguration } from "../../api/models/TenantResponse";
import { useCandidatePatch } from "../../api/hooks/useCandidatePatch";
import { buildJsonPatch } from "../../utils/buildJsonPatch";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    candidate: Partial<EntityCandidateForUpdate>;
    cId: string | undefined;
    eId: string | undefined;
    candidateId: string | undefined;
    candidateFieldsConfiguration: CandidateFieldsConfiguration | undefined | null;
}

type FormValues = { candidateCustomFields: CandidateCustomFields };

export const CandidateAdditionalModal: FC<Props> = ({ isOpen, onClose, candidate, cId, eId, candidateId, candidateFieldsConfiguration }) => {
    const toast = useToast();
    const patchCandidate = useCandidatePatch();

    const {
        register,
        handleSubmit,
        formState: { dirtyFields },
        reset,
    } = useForm<FormValues>({ values: { candidateCustomFields: candidate.candidateCustomFields ?? {} } });

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit: SubmitHandler<FormValues> = async data => {
        const ops = buildJsonPatch(dirtyFields as Record<string, unknown>, data as Record<string, unknown>);
        if (ops.length > 0) {
            await patchCandidate.mutateAsync({ clientId: cId, entityId: eId, candidateId, patch: ops });
        }
        toast({ title: "Saved", status: "success", duration: 3000, isClosable: true });
        handleClose();
    };

    const configuredFields = ([1, 2, 3, 4, 5] as const).filter(
        n => !!candidateFieldsConfiguration?.[`customField0${n}DisplayName` as keyof CandidateFieldsConfiguration]
    );

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size={"lg"}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Edit Additional Fields</ModalHeader>
                <ModalCloseButton />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <fieldset disabled={patchCandidate.isLoading}>
                            <VStack gap={"1rem"} alignItems={"start"}>
                                {configuredFields.map(n => {
                                    const displayName = candidateFieldsConfiguration?.[
                                        `customField0${n}DisplayName` as keyof CandidateFieldsConfiguration
                                    ] as string;
                                    return (
                                        <FormControl key={n}>
                                            <FormLabel>{displayName}:</FormLabel>
                                            <Input type="text" {...register(`candidateCustomFields.customField0${n}` as any)} />
                                        </FormControl>
                                    );
                                })}
                            </VStack>
                        </fieldset>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            type="submit"
                            variant={"primary"}
                            mr={3}
                            isLoading={patchCandidate.isLoading}
                            isDisabled={patchCandidate.isLoading}
                            loadingText={"Saving"}
                        >
                            Save
                        </Button>
                        <Button variant="ghost" onClick={handleClose}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};
